import {
  Component,
  DoCheck,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { LicenseType } from '@models/license';
import { TranslateService } from '@ngx-translate/core';
import { CourseItem, IlClass, Invite, Learner, TagFieldScopeEnum } from '@queries/generated';
import { LearnersService } from '@services/learners.service';
import { LicensesService } from '@services/licenses.service';
import { Fade } from '@util/animations';
import { IWizard } from '@util/wizard';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { first, map, tap } from 'rxjs/operators';

import { UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { ProductCatalogService } from '@services/product-catalog.service';
import { StorefrontsService } from '@services/storefronts.service';
import { SurveysService } from '@services/surveys.service';
import { insertIf } from '@util/insert-if';
import { BaseWizard } from '../base-wizard';
import { SidebarToggleComponent } from '../sidebar-toggle/sidebar-toggle.component';

interface Booking {
  license: LicenseType;
  ilClass: IlClass;
}

interface TrainingAssignment {
  license: LicenseType;
  bookings: Booking[];
}

interface PrimeRowSelectEvent {
  originalEvent: PointerEvent;
  data: Booking;
  index: number | undefined;
  type: 'row';
}

enum AssignTrainingStepsEnum {
  Learners,
  Tags,
  Training,
  Booking,
  Assign
}

export interface FilledTagField {
  key: string;
  required: boolean;
  value: string;
}

interface AssignTrainingWizard extends IWizard<AssignTrainingStepsEnum> {
  learners: Learner[];
  licenses: LicenseType[];
  bookings: Booking[];
  assignments: TrainingAssignment[];
}

@Component({
  selector: 'vnm-assign-training-wizard',
  templateUrl: './assign-training-wizard.component.html',
  styleUrls: ['./assign-training-wizard.component.scss'],
  animations: [Fade]
})
export class AssignTrainingWizardComponent extends BaseWizard implements OnInit, DoCheck, OnDestroy {
  @ViewChild('table') table: Table;

  @Input() learners: Learner[];
  @Input() licenses: LicenseType[];

  AssignTrainingStepsEnum = AssignTrainingStepsEnum;

  private wizardDiffer: KeyValueDiffer<string, any>;
  wizard: AssignTrainingWizard = {
    currentStep: AssignTrainingStepsEnum.Learners,
    steps: [],
    learners: [],
    licenses: [],
    bookings: [],
    running: false,
    assignments: [],
    errors: []
  };

  ownerOptions = [];
  selectedOwners = [];

  learners$ = this.learnersService.list();
  licenses$ = this.licenseService.licenseGroups$.pipe(
    map((licenses) =>
      licenses.filter((license) => license.availableSeats !== 0 && license.course?.deprecatedAt === null)
    ),
    tap((licenses) => (this.ownerOptions = this.buildOwnerOptions(licenses)))
  );

  hasILCourseSelection = false;
  hasILBookingSelection = false;
  trainingMessages = [];
  bookingMessages = [];
  totalAssigned = 0;
  tagsForm = this.fb.group({
    tagItems: this.fb.array([])
  });

  get tagItems(): UntypedFormArray {
    return this.tagsForm.controls['tagItems'] as UntypedFormArray;
  }

  constructor(
    private translateService: TranslateService,
    private learnersService: LearnersService,
    private licenseService: LicensesService,
    private messageService: MessageService,
    private catalogService: ProductCatalogService,
    private differs: KeyValueDiffers,
    public sidebarToggle: SidebarToggleComponent,
    private surveysService: SurveysService,
    private fb: UntypedFormBuilder,
    private storefrontsService: StorefrontsService
  ) {
    super();
  }

  ngOnInit() {
    this.wizardDiffer = this.differs.find(this.wizard).create();

    if (this.learners?.length) {
      this.wizard.learners = this.learners;
    }

    if (this.licenses) {
      this.wizard.licenses = this.licenses;
      this.ownerOptions = this.buildOwnerOptions(this.licenses);
    }

    /// Populates the tags form
    this.storefrontsService
      .tagFields(TagFieldScopeEnum.TrainingReporting)
      .pipe(first())
      .subscribe((tags) => {
        tags.forEach((tag) => {
          const childForm = this.fb.group({
            key: [tag.name],
            custom: [false],
            value: []
          });

          if (tag.required) {
            childForm.get('value').setValidators([Validators.required]);
          }

          this.tagItems.push(childForm);
        });

        this.initializeWizardSteps();
      });
  }

  ngDoCheck() {
    const changes = this.wizardDiffer.diff(this.wizard);
    if (changes) {
      changes.forEachChangedItem((record) => {
        switch (record.key) {
          case 'learners':
          case 'licenses':
            this.warnLicenseSeats();
            break;
          case 'bookings':
            this.warnBookingSeats();
            break;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.totalAssigned > 0) {
      this.surveysService.trigger('training-manager-distribute');
    }
  }

  get requiresTags(): boolean {
    return this.tagItems.value.length > 0;
  }

  override nextStep() {
    const automationId = this.wizard.steps[this.wizard.currentStep].automationId;

    switch (automationId) {
      case AssignTrainingStepsEnum.Training:
        if (!this.hasILCourseSelection) {
          this.assignTraining();
        } else {
          super.nextStep();
        }
        break;
      case AssignTrainingStepsEnum.Booking:
        this.assignTraining();
        break;
      default:
        super.nextStep();
        break;
    }
  }

  onBookingSelect(event: PrimeRowSelectEvent) {
    const { bookings } = this.wizard;
    const selectedBooking = event.data;

    const sameBookings = bookings.filter(
      (booking) =>
        booking.ilClass.ilCourse.id == selectedBooking.ilClass.ilCourse.id &&
        booking.license.id === selectedBooking.license.id
    );

    if (sameBookings.length > 1) {
      this.messageService.add({
        severity: 'warn',
        detail: this.translateService.instant('assign-training-wizard.booking-step.duplicate-dates')
      });

      const firstBooking = bookings.findIndex(
        (booking) =>
          booking.ilClass.ilCourse.id == selectedBooking.ilClass.ilCourse.id &&
          booking.license.id === selectedBooking.license.id
      );
      bookings.splice(firstBooking, 1);
      this.wizard.bookings = [...bookings];
    }
  }

  onFilterOwners(event) {
    this.table.filter(event.value, 'owner.gid', 'in');
  }

  warnLicenseSeats() {
    const { learners, licenses } = this.wizard;
    const learnerCount = learners.length;

    this.hasILCourseSelection = false;
    this.trainingMessages = [];
    licenses.forEach((license) => {
      const { availableSeats, course, seats } = license;

      if (!this.hasILCourseSelection) {
        this.hasILCourseSelection = course.courseItems?.some((item) => item.ilCourse !== null);
      }

      if (seats !== null && availableSeats < learnerCount) {
        const diffCount = learnerCount - license.availableSeats;
        this.trainingMessages = [
          {
            severity: 'warn',
            detail: this.translateService.instant('assign-training-wizard.training-step.exceeds-seats', {
              course: course.name,
              learners: diffCount
            })
          },
          ...this.trainingMessages
        ];
      }
    });
  }

  warnBookingSeats() {
    const { bookings, learners } = this.wizard;
    const learnerCount = learners.length;

    this.hasILBookingSelection = bookings.length > 0;
    this.bookingMessages = [];
    bookings.forEach((booking) => {
      const { availableSeats, maxCapacity, ilCourse } = booking.ilClass;

      if (maxCapacity !== null && availableSeats < learnerCount) {
        const diffCount = learnerCount - availableSeats;
        this.bookingMessages = [
          {
            severity: 'warn',
            detail: this.translateService.instant('assign-training-wizard.booking-step.exceeds-seats', {
              course: ilCourse.name,
              learners: diffCount
            })
          },
          ...this.bookingMessages
        ];
      }
    });
  }

  hasILCourse(courseItems: CourseItem[]) {
    return courseItems?.some((item) => item.ilCourse !== null);
  }

  countILCourseItems(courseItems: CourseItem[]) {
    return courseItems?.reduce((n, item) => {
      return n + Number(item.ilCourse !== null);
    }, 0);
  }

  countILBookingSelections(license: LicenseType) {
    const { bookings } = this.wizard;
    return bookings.reduce((n, booking) => {
      return n + Number(booking.license.id === license.id);
    }, 0);
  }

  assignTraining() {
    /// Since steps are dynamic, let's got directly to the last of them,
    /// independent of how many we have.
    this.wizard.currentStep = this.wizard.steps.length - 1;
    this.wizard.running = true;
    this.wizard.errors = [];

    const { licenses, bookings } = this.wizard;

    const assignments = licenses.map((license) => ({
      license,
      bookings: bookings.filter((booking) => booking.license.gid === license.gid)
    }));

    this.wizard.assignments = assignments;
  }

  onBrowseCatalog() {
    this.sidebarToggle?.close();
    this.catalogService.presentCatalog();
  }

  onAssigned(invites: RecursivePartial<Invite>[]) {
    this.totalAssigned += invites.length;
  }

  addOption() {
    this.tagItems.push(
      this.fb.group({
        key: [null, Validators.required],
        custom: [true],
        value: [null, Validators.required]
      })
    );
  }

  removeOption(index: number) {
    this.tagItems.removeAt(index);
  }

  /**
   * Initialize the steps of the wizard and its initial step.
   * We only includes the Tags step if there's any tag fields available.
   * We also set the initial step based on the criterias, but this is outsourced to another method.
   */
  private initializeWizardSteps() {
    this.wizard.steps = [
      {
        label: this.translateService.instant('assign-training-wizard.learners-step.title'),
        automationId: AssignTrainingStepsEnum.Learners
      },
      ...insertIf(this.requiresTags, {
        label: this.translateService.instant('assign-training-wizard.tags-step.title'),
        automationId: AssignTrainingStepsEnum.Tags
      }),
      {
        label: this.translateService.instant('assign-training-wizard.training-step.title'),
        automationId: AssignTrainingStepsEnum.Training
      },
      {
        label: this.translateService.instant('assign-training-wizard.booking-step.title'),
        automationId: AssignTrainingStepsEnum.Booking
      },
      {
        label: this.translateService.instant('assign-training-wizard.assign-step.title'),
        automationId: AssignTrainingStepsEnum.Assign
      }
    ];

    /// Figure out the index of the current step based on the returned automationId
    /// and set to the wizard.
    const automationId = this.guessWizardInitialStep();
    this.wizard.currentStep = this.wizard.steps.findIndex((a) => a.automationId == automationId);
  }

  /**
   * If we have tags, we can start on it first, if we already have the learner(s).
   * But if we don't have tags, but still having the learner, we can start choosing the training(s).
   * Otherwise, we should start choosing the learners.
   *
   * We'll return the automationId of the step to start with, and then whoever calls this method
   * will need to handle that and find the index of the step with this automationId.
   */
  private guessWizardInitialStep(): AssignTrainingStepsEnum {
    if (this.requiresTags && this.wizard.learners.length) {
      return AssignTrainingStepsEnum.Tags;
    } else if (this.wizard.learners.length) {
      return AssignTrainingStepsEnum.Training;
    } else {
      return AssignTrainingStepsEnum.Learners;
    }
  }

  private buildOwnerOptions(licenses: RecursivePartial<LicenseType[]>) {
    this.selectedOwners = [];
    const licenseMap = licenses.reduce((owners, license) => {
      owners[license.owner.gid] ??= license.owner;
      return owners;
    }, {});
    return Object.values(licenseMap);
  }
}
